/* <gr-drawer> — the shared drawer shell.
   Owns scrim, open/close, Escape, focus containment, focus restoration,
   background inert and scroll lock. The mobile nav uses it directly; the cart
   drawer extends it. Every listener is registered in connectedCallback and
   removed in disconnectedCallback so a Theme Editor re-render can never leave
   a duplicate handler or a locked <body> behind. */

class GrDrawer extends HTMLElement {
  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.releaseFocus = null;
    this.opener = null;
  }

  connectedCallback() {
    // connectedCallback can run more than once for the same element; make it idempotent.
    if (this._bound) return;
    this._bound = true;

    this.panel = this.querySelector('[data-drawer-panel]');
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);

    // Any control anywhere may open this drawer by id.
    this.triggerSelector = '[data-drawer-open="' + this.id + '"]';
    this.updateTriggerState(false);
    this.onTriggerClick = (event) => {
      const trigger = event.target.closest(this.triggerSelector);
      if (!trigger) return;
      // The trigger is a real link to /cart (or the menu page) so it still works
      // without JS; we only take over once JS is present.
      event.preventDefault();
      this.open(trigger);
    };
    document.addEventListener('click', this.onTriggerClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onTriggerClick);
    this.removeEventListener('click', this.onClick);
    if (this.hasAttribute('open')) {
      GR.unlockScroll();
      this.setBackgroundInert(false);
    }
    if (this.releaseFocus) this.releaseFocus();
    if (this.triggerSelector) this.updateTriggerState(false);
    this._bound = false;
  }

  onClick(event) {
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.hasAttribute('open')) {
      event.stopPropagation();
      this.close();
    }
  }

  updateTriggerState(expanded) {
    document.querySelectorAll(this.triggerSelector).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /* Marks everything except this drawer as inert so assistive tech and Tab
     cannot reach the page behind the scrim. */
  setBackgroundInert(state) {
    Array.from(document.body.children).forEach((child) => {
      if (child === this || child.contains(this)) return;
      if (child.id === 'gr-live-region') return;
      if (state) {
        if (child.hasAttribute('inert')) return;
        child.setAttribute('inert', '');
        child.setAttribute('data-gr-inert', '');
      } else if (child.hasAttribute('data-gr-inert')) {
        child.removeAttribute('inert');
        child.removeAttribute('data-gr-inert');
      }
    });
  }

  open(opener) {
    if (this.hasAttribute('open')) return;

    // Only one drawer may be open at a time.
    document.querySelectorAll('gr-drawer[open], gr-cart-drawer[open]').forEach((other) => {
      if (other !== this) other.close();
    });

    this.opener = opener || document.activeElement;
    this.setAttribute('open', '');
    this.updateTriggerState(true);
    GR.lockScroll();
    this.setBackgroundInert(true);

    const first = this.querySelector('[data-drawer-initial-focus]') || this.querySelector('[data-drawer-close]');
    this.releaseFocus = GR.trapFocus(this.panel || this, first);
    this.dispatchEvent(new CustomEvent('gr:drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    this.updateTriggerState(false);
    GR.unlockScroll();
    this.setBackgroundInert(false);
    if (this.releaseFocus) { this.releaseFocus(); this.releaseFocus = null; }

    // Restore focus to whatever opened the drawer, if it is still in the DOM.
    if (this.opener && document.contains(this.opener)) this.opener.focus();
    this.opener = null;
    this.dispatchEvent(new CustomEvent('gr:drawer:close', { bubbles: true }));
  }
}

if (!customElements.get('gr-drawer')) customElements.define('gr-drawer', GrDrawer);
window.GrDrawer = GrDrawer;
